import { useState, useRef, useCallback } from "react";
import { Instagram, Share2, Download, Check, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import QRCode from "qrcode";
import { haptic } from "../utils/haptic";

/**
 * Instagram Stories Share Component
 * Generates a dynamic canvas image with booking details for sharing
 */
const InstagramShareCard = ({ 
  booking,
  userName = "Music Lover",
  trackTitle = "Progressive House Loop",
  eventName = "Premium Event",
  eventCity = "London",
  eventDate = "",
  qrData = "",
  bookingId = "XTRIP001"
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const canvasRef = useRef(null);

  // Generate the shareable image
  const generateShareImage = useCallback(async () => {
    setIsGenerating(true);
    haptic("medium");

    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      
      // Instagram Stories dimensions (9:16 ratio)
      canvas.width = 1080;
      canvas.height = 1920;

      // Background gradient (dark glassmorphism style)
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, "#080b10");
      gradient.addColorStop(0.5, "#0d1117");
      gradient.addColorStop(1, "#080b10");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add noise/grain texture
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        const noise = (Math.random() - 0.5) * 15;
        data[i] = Math.max(0, Math.min(255, data[i] + noise));
        data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise));
        data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise));
      }
      ctx.putImageData(imageData, 0, 0);

      // Glassmorphic card background
      ctx.fillStyle = "rgba(255, 255, 255, 0.03)";
      ctx.roundRect(60, 200, canvas.width - 120, 1520, 40);
      ctx.fill();
      
      // Card border
      ctx.strokeStyle = "rgba(255, 255, 255, 0.1)";
      ctx.lineWidth = 2;
      ctx.stroke();

      // XTrip Logo
      ctx.fillStyle = "#ff0000";
      ctx.roundRect(100, 280, 60, 60, 8);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 28px 'Inter', sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("XT", 130, 320);

      // Brand name
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 36px 'Inter', sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("XTRIP", 180, 325);

      // Tagline badge
      ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
      ctx.roundRect(100, 360, 280, 36, 18);
      ctx.fill();
      ctx.fillStyle = "#ff0000";
      ctx.font = "600 14px 'Space Mono', monospace";
      ctx.fillText("TRAVEL FOR THE NIGHT", 120, 384);

      // Event Name (main headline)
      ctx.fillStyle = "#ffffff";
      ctx.font = "900 64px 'Playfair Display', serif";
      ctx.textAlign = "center";
      // Word wrap for long names
      const words = eventName.split(" ");
      let line = "";
      let y = 520;
      const maxWidth = canvas.width - 200;
      
      for (let word of words) {
        const testLine = line + word + " ";
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && line !== "") {
          ctx.fillText(line.trim(), canvas.width / 2, y);
          line = word + " ";
          y += 80;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line.trim(), canvas.width / 2, y);

      // Event details
      const detailsY = y + 100;
      ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
      ctx.font = "500 28px 'Inter', sans-serif";
      ctx.fillText(`${eventCity} • ${eventDate}`, canvas.width / 2, detailsY);

      // Horizontal divider
      ctx.strokeStyle = "rgba(255, 255, 255, 0.1)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(150, detailsY + 60);
      ctx.lineTo(canvas.width - 150, detailsY + 60);
      ctx.stroke();

      // User section
      const userY = detailsY + 140;
      ctx.fillStyle = "#ffffff";
      ctx.font = "600 24px 'Inter', sans-serif";
      ctx.fillText("BOOKED BY", canvas.width / 2, userY);
      ctx.fillStyle = "#ff0000";
      ctx.font = "bold 42px 'Playfair Display', serif";
      ctx.fillText(userName, canvas.width / 2, userY + 60);

      // Vibe section (track info)
      const vibeY = userY + 140;
      ctx.fillStyle = "rgba(0, 255, 157, 0.2)";
      ctx.roundRect(canvas.width / 2 - 180, vibeY - 20, 360, 50, 25);
      ctx.fill();
      ctx.fillStyle = "#00ff9d";
      ctx.font = "600 18px 'Space Mono', monospace";
      ctx.fillText(`♫ ${trackTitle}`, canvas.width / 2, vibeY + 10);

      // QR Code
      const qrY = vibeY + 100;
      const qrSize = 280;
      const qrX = (canvas.width - qrSize) / 2;
      
      // QR container
      ctx.fillStyle = "#ffffff";
      ctx.roundRect(qrX - 20, qrY - 20, qrSize + 40, qrSize + 40, 20);
      ctx.fill();

      // Generate QR code
      const qrDataUrl = await QRCode.toDataURL(qrData || `https://electronic-hub-15.preview.emergentagent.com/booking/${bookingId}`, {
        width: qrSize,
        margin: 0,
        color: {
          dark: "#080b10",
          light: "#ffffff"
        },
        errorCorrectionLevel: "H"
      });

      // Draw QR code
      const qrImage = new Image();
      await new Promise((resolve) => {
        qrImage.onload = resolve;
        qrImage.src = qrDataUrl;
      });
      ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

      // Booking ID under QR
      ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
      ctx.font = "600 20px 'Space Mono', monospace";
      ctx.fillText(`#${bookingId}`, canvas.width / 2, qrY + qrSize + 50);

      // Footer stats
      const footerY = qrY + qrSize + 120;
      ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
      ctx.font = "500 16px 'Inter', sans-serif";
      ctx.fillText("Powered by Resident Advisor • Duffel • Stripe", canvas.width / 2, footerY);

      // 128BPM badge at bottom
      ctx.fillStyle = "rgba(255, 0, 0, 0.15)";
      ctx.roundRect(canvas.width / 2 - 100, footerY + 30, 200, 40, 20);
      ctx.fill();
      ctx.strokeStyle = "rgba(255, 0, 0, 0.3)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = "#ff0000";
      ctx.font = "bold 16px 'Space Mono', monospace";
      ctx.fillText("128BPM TRAVEL ENGINE", canvas.width / 2, footerY + 56);

      // Convert to blob
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/png", 0.95);
      });
    } catch (error) {
      console.error("[InstagramShare] Generation error:", error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  }, [eventName, eventCity, eventDate, userName, trackTitle, qrData, bookingId]);

  // Handle share action
  const handleShare = async () => {
    try {
      const blob = await generateShareImage();
      const file = new File([blob], `xtrip-${bookingId}.png`, { type: "image/png" });

      // Check for native share with files support
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `XTrip Booking: ${eventName}`,
          text: `Just booked my trip to ${eventName} in ${eventCity}! 🎶✈️ #XTrip #TravelForTheNight`,
          files: [file]
        });
        setIsShared(true);
        haptic("success");
        toast.success("Shared successfully!");
      } else if (navigator.share) {
        // Fallback to text-only share
        await navigator.share({
          title: `XTrip Booking: ${eventName}`,
          text: `Just booked my trip to ${eventName} in ${eventCity}! 🎶✈️\n\nBook yours at https://electronic-hub-15.preview.emergentagent.com`,
          url: `https://electronic-hub-15.preview.emergentagent.com/events`
        });
        setIsShared(true);
        haptic("success");
        toast.success("Shared successfully!");
      } else {
        // Download as fallback
        handleDownload();
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("[InstagramShare] Share error:", error);
        handleDownload();
      }
    }
  };

  // Download the image
  const handleDownload = async () => {
    try {
      const blob = await generateShareImage();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `xtrip-${bookingId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      haptic("success");
      toast.success("Image downloaded! Share it on Instagram Stories");
    } catch (error) {
      console.error("[InstagramShare] Download error:", error);
      toast.error("Failed to generate image");
    }
  };

  return (
    <div className="space-y-3">
      <Button
        onClick={handleShare}
        disabled={isGenerating}
        className="w-full bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 group"
        data-testid="share-instagram-btn"
      >
        {isGenerating ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Generating...
          </>
        ) : isShared ? (
          <>
            <Check className="w-5 h-5" />
            Shared!
          </>
        ) : (
          <>
            <Instagram className="w-5 h-5 group-hover:scale-110 transition-transform" />
            Share to Stories
          </>
        )}
      </Button>

      <Button
        onClick={handleDownload}
        disabled={isGenerating}
        variant="outline"
        className="w-full border-border text-muted-foreground hover:text-foreground flex items-center justify-center gap-2"
        data-testid="download-ticket-btn"
      >
        <Download className="w-4 h-4" />
        Download Digital Ticket
      </Button>
    </div>
  );
};

export default InstagramShareCard;
